import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import ReactTooltip from 'react-tooltip';
import { useRequest } from 'ahooks';
import { RootState } from '@/models';
import Layout from '@/layouts';
import Footer from '@/components/footer';
import {
  getProductBrandList,
  getProductCaseList,
  getProductDetail,
  getProductFunction,
  getProductPrice,
  queryHasUserConsulted,
} from '@/services/product';
import { Product, ProductBrand, ProductCase } from '@/interface/product';
import InformationQuote from '../components/InformationQuote';
import ProductHeaderBanner from '../components/ProductHeaderBanner';
import ProductTabList, { ProductTab } from '../components/ProductTabList';
import ProductInfo from '../components/ProductInfo';
import ProductFunction from '../components/ProductFunction';
import CooperationBrand from '../components/CooperationBrand';
import BusinessCase from '../components/BusinessCase';
import styles from './index.module.scss';

export const getServerSideProps: GetServerSideProps<
  Product & {
    initialProductTabList?: ProductTab[];
    companyEstablishedTime?: string;
    functionContent?: string;
    priceContent?: string;
    initialProductCaseList?: ProductCase[];
    productCaseListTotal?: number;
    initialProductBrandList?: ProductBrand[];
    productBrandListTotal?: number;
  },
  { id: string }
> = async ({ params, res }) => {
  res.setHeader('Cache-Control', 'public, s-maxage=59, stale-while-revalidate=119');
  try {
    const [
      { data: productDetail },
      { data: productFunction },
      { data: productPrice },
      { data: productCaseListRes },
      { data: productBrandListRes },
    ] = await Promise.all([
      getProductDetail(params.id).catch(() => {
        const data: Product = {};
        return { data };
      }),
      getProductFunction(params.id).catch(() => {
        const data = { functionContent: '' };
        return { data };
      }),
      getProductPrice(params.id).catch(() => {
        const data = { priceContent: '' };
        return { data };
      }),
      getProductCaseList({ boxProductId: params.id, pageIndex: 1, pageSize: 6 }).catch(() => {
        const data = { list: [], total: 0 };
        return { data };
      }),
      getProductBrandList({ boxProductId: params.id, pageIndex: 1, pageSize: 15 }).catch(() => {
        const data = { list: [], total: 0 };
        return { data };
      }),
    ]);
    const initialProductTabList = [{ name: '产品信息', anchorElementId: '#productInfo' }];
    // console.log(productDetail);
    // console.log(productFunction);
    // console.log(productPrice);
    // console.log(productCaseListRes);
    // console.log(productBrandListRes);
    if (productFunction?.functionContent) {
      initialProductTabList.push({ name: '产品功能', anchorElementId: '#productFunction' });
    }
    if (productCaseListRes.list.length) {
      initialProductTabList.push({ name: '行业案例', anchorElementId: '#businessCase' });
    }
    if (productBrandListRes.list.length) {
      initialProductTabList.push({ name: '合作品牌', anchorElementId: '#cooperationBrand' });
    }
    initialProductTabList.push({ name: '产品定价', anchorElementId: '#productPricing' });
    const { companyRegisterYear, companyRegisterMonth, companyRegisterDay } = productDetail;
    let companyEstablishedTime = '';
    if (companyRegisterYear) {
      companyEstablishedTime += `${companyRegisterYear}年`;
    }
    if (companyRegisterMonth) {
      companyEstablishedTime += `${companyRegisterMonth}月`;
    }
    if (companyRegisterDay) {
      companyEstablishedTime += `${companyRegisterDay}日`;
    }
    return {
      props: {
        initialProductTabList,
        ...productDetail,
        companyEstablishedTime,
        ...productFunction,
        ...productPrice,
        initialProductCaseList: productCaseListRes.list,
        productCaseListTotal: productCaseListRes.total,
        initialProductBrandList: productBrandListRes.list,
        productBrandListTotal: productBrandListRes.total,
      },
    };
  } catch (e) {
    return {
      props: {},
    };
  }
};
const PRODUCT_HEADER_BANNER_HEIGHT = 240;
function ProductDetailPage({
  initialProductTabList,
  boxProductId,
  productName,
  productLogo,
  productDesc,
  companyName,
  companyEstablishedTime,
  companyAddress,
  companyFinancing,
  companyEmail,
  companyWebsite,
  contactPhone,
  customerServiceWechatUrl,
  imgUrlList,
  functionContent,
  priceContent,
  initialProductCaseList,
  productCaseListTotal,
  initialProductBrandList,
  productBrandListTotal,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const DynamicProductPricing = dynamic(() => import('../components/DynamicProductPricing'));
  const { isClickConsult } = useSelector((state: RootState) => state.cbndatabox);
  const dispatch = useDispatch();
  const headerRef = useRef<HTMLHeadElement>(null);
  const tabListRef = useRef<HTMLElement>(null);
  const { token } = useSelector((state: RootState) => state.user);
  const { data: hasConsultedRes, refresh } = useRequest(() => queryHasUserConsulted({ boxProductId }), {
    // ready: !!token,
    refreshDeps: [token],
  });
  // console.log('hasConsultedRes:', hasConsultedRes);
  // 登录成功后调用接口，查询用户是否询价过该产品，若没有询价过且在之前未登录的时候点击了免费咨询按钮，弹出询价表单Modal
  const loginCallback = useCallback(async () => {
    const res = await queryHasUserConsulted({ boxProductId });
    // console.log(res);
    if (!res?.data?.hasSubmit && isClickConsult) {
      dispatch({
        type: 'modal/open',
        payload: {
          content: <InformationQuote boxProduct={{ productName, boxProductId }} onSubmitSusccess={refresh} />,
        },
      });
    }
  }, [boxProductId, dispatch, isClickConsult, productName, refresh]);

  useEffect(() => {
    const scrollHandler = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      const headerHeight = PRODUCT_HEADER_BANNER_HEIGHT - scrollTop >= 0 ? PRODUCT_HEADER_BANNER_HEIGHT - scrollTop : 0;
      const tablistMarginTop =
        scrollTop - PRODUCT_HEADER_BANNER_HEIGHT <= 0 ? scrollTop - PRODUCT_HEADER_BANNER_HEIGHT : 0;
      window.requestAnimationFrame(() => {
        headerRef.current.style.height = `${headerHeight}px`;
        if (PRODUCT_HEADER_BANNER_HEIGHT - scrollTop <= 24) {
          tabListRef.current.style.marginTop = `${tablistMarginTop}px`;
        }
      });
    };
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  }, []);
  return (
    <>
      <Layout
        title="Martech企服商店-企服点评平台-SaaS应用商店 | CBNData"
        description="第一财经商业数据中心（CBNData）隶属于上海文化广播影视集团（SMG），是国内领先的消费研究机构及数字化增长服务商。CBNDataBox是专业的Martech企服应用商店，围绕企业内营销流程的需求场景，为消费行业的营销&运营人员整合优质的Martech企服工具。"
        keyword="企业服务,企业服务工具,CBNData企服商店,公司点评,企服点评,SaaS商店,应用商店,大数据,SaaS服务,客户管理,营销数据,市场营销,创业服务,营销决策,管理工具"
        isShowNavBar
        FooterComponent={Footer}
        mainClass={styles.mainContentWrapper}
        loginCallback={loginCallback}
      >
        <div className={styles.productHeaderNav}>
          <ProductHeaderBanner
            ref={headerRef}
            className={styles.productHeaderBanner}
            productName={productName}
            productLogo={productLogo}
          />
          <ProductTabList ref={tabListRef} className={styles.productTabList} tabList={initialProductTabList} />
        </div>
        <ProductInfo
          productDesc={productDesc}
          companyName={companyName}
          companyAddress={companyAddress}
          companyEstablishedTime={companyEstablishedTime}
          companyFinancing={companyFinancing}
          imgUrlList={imgUrlList}
        />
        {!!functionContent && <ProductFunction functionContent={functionContent} />}
        {!!initialProductCaseList?.length && (
          <BusinessCase
            boxProductId={boxProductId}
            initialProductCaseList={initialProductCaseList}
            productCaseListTotal={productCaseListTotal}
          />
        )}
        {!!initialProductBrandList?.length && (
          <CooperationBrand
            boxProductId={boxProductId}
            initialProductBrandList={initialProductBrandList}
            productBrandListTotal={productBrandListTotal}
          />
        )}
        <DynamicProductPricing
          hasConsulted={hasConsultedRes?.data?.hasSubmit}
          boxProductId={boxProductId}
          companyEmail={companyEmail}
          companyWebsite={companyWebsite}
          contactPhone={contactPhone}
          customerServiceWechatUrl={customerServiceWechatUrl}
          priceContent={priceContent}
          boxProduct={{ productName, boxProductId }}
          onConsultSuccess={refresh}
        />
      </Layout>
      <ReactTooltip id="brands" arrowColor="transparent" className={styles.toolTip} />
    </>
  );
}

export default ProductDetailPage;
